import { Controller } from 'stimulus'
import { AnnotationManager } from '../../_js/components/annotation-manager'
import ScrollParallax from '../../_js/animations/scroll-parallax'

export default class CollaborateController extends Controller {
  static targets = ['titleLine']

  static values = {
    spawnDelay: Number,
    initialDelay: Number,
  }

  connect() {
    // Initialize user annotation system
    this.setupUserAnnotations()

    // Initialize parallax system
    this.setupParallax()

    // Initialize cursor system demo
    this.setupCursorDemo()
  }

  /**
   * Setup user annotation system
   */
  setupUserAnnotations() {
    // Create and initialize annotation manager
    this.annotationManager = new AnnotationManager()
    this.annotationManager.initialize()
  }

  /**
   * Setup simple parallax system for hero elements
   */
  setupParallax() {
    // Configure hero elements showcasing new enhanced features
    const parallaxElements = [
      // Backward compatible: ID-based with simple multiplier
      {
        id: 'hero__parallax__sketch-wrapper',
        multiplier: -0.1,
        onlyInView: true,
      },

      // Enhanced: Responsive multipliers for different screen sizes
      {
        id: 'hero__parallax__sketch-web-wrapper',
        multiplier: {
          xs: -0.05, // Disabled on mobile for performance
          s: -0.1, // Subtle on small screens
          m: -0.2, // Normal on tablets
          l: -0.25, // Strong on desktop
          xl: -0.25, // Same on XL
        },
        onlyInView: true,
      },
      {
        id: 'hero__parallax__sketch-web-tagline-p',
        multiplier: {
          xs: -0.05, // Disabled on mobile
          s: -0.1, // Subtle on small
          m: -0.1, // Normal on tablets
          l: -0.1, // Strong on desktop
          xl: -0.1,
        },
        onlyInView: true,
      },
      {
        id: 'hero__parallax__sketch-web-tagline-avatars',
        multiplier: {
          xs: 0, // Disabled on mobile
          s: -0.175, // Subtle on small
          m: -0.175, // Normal on tablets
          l: -0.175, // Strong on desktop
          xl: -0.175,
        },
        onlyInView: true,
      },

      // iOS section with responsive multipliers
      {
        id: 'hero__parallax__sketch-ios-wrapper',
        multiplier: {
          xs: 0, // Disabled on mobile for performance
          s: -0.1, // Subtle on small screens
          m: -0.2, // Normal on tablets
          l: -0.25, // Strong on desktop
          xl: -0.25, // Same on XL
        },
        onlyInView: true,
      },
      {
        id: 'hero__parallax__sketch-ios-tagline-p',
        multiplier: {
          xs: -0.05, // Disabled on mobile
          s: -0.1, // Subtle on small
          m: -0.1, // Normal on tablets
          l: -0.1, // Strong on desktop
          xl: -0.1,
        },
        onlyInView: true,
      },
      {
        id: 'hero__parallax__sketch-ios-tagline-avatars',
        multiplier: {
          xs: 0, // Disabled on mobile
          s: -0.175, // Subtle on small
          m: -0.175, // Normal on tablets
          l: -0.175, // Strong on desktop
          xl: -0.175,
        },
        onlyInView: true,
      },
    ]

    // Global parallax options
    const parallaxOptions = {
      onlyInView: false, // Global default (individual elements can override)
      viewportOffset: 200, // Buffer zone for smoother transitions
    }

    this.parallaxSystem = new ScrollParallax(parallaxElements, parallaxOptions)
  }

  /**
   * Clean up when controller disconnects
   */
  disconnect() {
    // Clean up annotation manager
    if (this.annotationManager) {
      this.annotationManager.destroy()
      this.annotationManager = null
    }

    // Clean up parallax system
    if (this.parallaxSystem) {
      this.parallaxSystem.destroy()
      this.parallaxSystem = null
    }
  }

  /**
   * Setup cursor system demo
   */
  setupCursorDemo() {
    // Wait for page to fully load and DOM to be ready
    const spawnDelay = this.hasSpawnDelayValue ? this.spawnDelayValue : 500

    // Wait for DOM to be fully loaded and rendered
    if (document.readyState === 'complete') {
      setTimeout(() => this.startCollaborationDemo(), spawnDelay)
    } else {
      // Wait for the page to fully load before starting demo
      window.addEventListener('load', () => {
        setTimeout(() => this.startCollaborationDemo(), spawnDelay)
      })
    }
  }

  /**
   * Start collaboration demo with multiple cursors
   */
  startCollaborationDemo() {
    // Check if cursor manager is available
    const cursorManagerElement = document.querySelector('[data-controller*="cursor"]')
    if (!cursorManagerElement) {
      return
    }

    // Get cursor manager controller
    const cursorManager = this.application.getControllerForElementAndIdentifier(cursorManagerElement, 'cursor')

    if (!cursorManager) {
      return
    }

    /** Avatars & Cursors attached to the Cloud Web Tagline */
    const dianaAvatar = cursorManager.spawn({
      name: 'Diana',
      color: 'pink',
      anchored: true,
      anchoredToTarget: '#hero__parallax__sketch-web-tagline-avatars',
      relativeOffset: { x: 200, y: 150 },
      displayMode: 'avatar',
      avatarPath: '/assets/avatars/saafia@2x.png',
      className: 'collab__entity--avatar-idling-1',
      zIndex: 10,
    })

    const maggieAvatar = cursorManager.spawn({
      name: 'Maggie',
      color: 'purple',
      anchored: true,
      anchoredToTarget: '#hero__parallax__sketch-web-tagline-avatars',
      relativeOffset: { x: -100, y: -100 },
      displayMode: 'avatar',
      avatarPath: '/assets/avatars/alex@2x.png',
      className: 'collab__entity--avatar-idling-1',
      zIndex: 10,
    })

    const bobAvatar = cursorManager.spawn({
      name: 'Bob',
      color: 'blue',
      anchored: true,
      anchoredToTarget: '#hero__parallax__sketch-web-tagline-avatars',
      relativeOffset: { x: 50, y: 150 },
      avatarPath: '/assets/avatars/alex@2x.png',
      className: 'collab__entity--avatar-idling-1',
      zIndex: 10,
    })

    /** Avatars & Cursors attached to the Cloud iOS Tagline */
    const janeAvatar = cursorManager.spawn({
      name: 'Alex',
      color: 'purple',
      anchored: true,
      anchoredToTarget: '#hero__parallax__sketch-ios-tagline-avatars',
      relativeOffset: { x: -100, y: 100 },
      displayMode: 'avatar',
      avatarPath: '/assets/avatars/alex@2x.png',
      className: 'collab__entity--avatar-idling-1',
      zIndex: 10,
    })

    const selenaAvatar = cursorManager.spawn({
      name: 'Selena',
      color: 'green',
      anchored: true,
      anchoredToTarget: '#hero__parallax__sketch-ios-tagline-avatars',
      relativeOffset: { x: -50, y: -50 },
      avatarPath: '/assets/avatars/alex@2x.png',
      className: 'collab__entity--avatar-idling-1',
      zIndex: 10,
    })

    const gomezAvatar = cursorManager.spawn({
      name: 'Gomez',
      color: 'pink',
      anchored: true,
      anchoredToTarget: '#hero__parallax__sketch-ios-tagline-avatars',
      relativeOffset: { x: 50, y: 250 },
      avatarPath: '/assets/avatars/alex@2x.png',
      className: 'collab__entity--avatar-idling-1',
      zIndex: 10,
    })

    // main-nav__logo

    /**
     * Active Cursors in the Hero Section
     */
    const aliceCursor = cursorManager.spawn({
      name: 'Alice',
      color: 'purple',
      position: { x: 300, y: 400 },
      initialDelay: 500,
    })

    const freddieCursor = cursorManager.spawn({
      name: 'Freddie',
      color: 'blue',
      initialDelay: 1000,
      relativeToTarget: '.section--hero .sketch__window',
      relativeOffset: { x: 1200, y: -150 },
    })

    const dianaCursor = cursorManager.spawn({
      name: 'Diana',
      color: 'pink',
      relativeToTarget: '.section--hero .sketch__window',
      relativeOffset: { x: 850, y: -150 },
      initialDelay: 700,
    })

    const idleStandard = {
      type: 'idle',
      duration: 4000, // Duration of each idle cycle (for finite idle)
      radius: 80, // Maximum distance from center point
      variation: 0.6, // Randomness factor
      className: 'collab__entity--idle',
      infinite: true, // Stay in idle state indefinitely

      // Realistic idle parameters
      minPause: 1000, // Minimum pause between movements (ms)
      maxPause: 1500, // Maximum pause between movements (ms)
      minMoveDuration: 800, // Minimum movement duration (ms)
      maxMoveDuration: 1500, // Maximum movement duration (ms)
      fidgetChance: 0.7, // Chance of making a fidget movement (0-1)
    }

    const idleCalm = {
      ...idleStandard,
      radius: 40,
    }

    const idleCalmest = {
      ...idleStandard,
      radius: 10,
      duration: 8000,
    }

    const idleForAMoment = {
      ...idleStandard,
      infinite: false,
      duration: 4500,
    }

    /**
     * Instructions
     */
    // Designer workflow
    aliceCursor.addInstructions([
      // Select title
      { type: 'moveTo', target: '#hero__title', delay: 0 },
      { type: 'action', action: 'select', target: '#hero__title', delay: 150 },

      // Change title text
      {
        type: 'action',
        action: 'change-attributes',
        target: '#hero__title > span',
        styles: {
          textDecoration: 'underline',
        },
        delay: 350,
      },

      // Move to sketch window and select it
      { type: 'moveTo', target: '#hero-blurb__real-time', delay: 350 },
      { type: 'action', action: 'select', target: '#hero__blurb', delay: 250 },
      {
        type: 'action',
        action: 'change-attributes',
        target: '#hero-blurb__real-time',
        styles: {
          fontWeight: 'bold',
        },
        delay: 200,
      },
      { type: 'moveTo', target: '#hero-blurb__developer-handoff', delay: 150 },
      {
        type: 'action',
        action: 'change-attributes',
        target: '#hero-blurb__developer-handoff',
        styles: {
          fontWeight: 'bold',
        },
        delay: 350,
      },
      {
        type: 'action',
        target: '#hero__blurb',
        action: 'deselect',
        delay: 350,
      },
      {
        type: 'moveTo',
        relative: { x: -200, y: 30 },
        delay: 400,
        duration: 800,
      },

      idleStandard,
    ])

    freddieCursor.addInstructions([
      {
        type: 'moveTo',
        relative: { x: 60, y: -100 },
        delay: 400,
        duration: 800,
      },
      { type: 'moveTo', target: '.hero__cta__secondary', delay: 800 },
      { type: 'action', action: 'select', target: '.hero__cta__secondary', delay: 250 },
      {
        type: 'action',
        action: 'change-attributes',
        target: '.hero__cta__secondary',
        html: `Learn more <svg style="margin-left: 4px; margin-right: -4px;" xmlns="http://www.w3.org/2000/svg" width="24" height="24" color="currentcolor" class="section__cta__icon">
  <g stroke="currentcolor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
    <path d="M13 8l4 4-4 4M15.5 12h-9"></path>
  </g>
</svg>`,
        delay: 200,
      },
      { type: 'moveTo', target: '#hero__cta__get-started', delay: 300 },
      { type: 'action', action: 'select', target: '#hero__cta__get-started', delay: 250 },
      {
        type: 'action',
        action: 'change-attributes',
        target: '#hero__cta__get-started',
        text: 'Get started for free',
        delay: 200,
      },
      { type: 'action', action: 'deselect', target: '#hero__cta__get-started', delay: 250 },
      // Move to first annotation and reveal it
      {
        type: 'moveTo',
        relative: { x: 40, y: -120 }, // Move 100px left, 50px down
        delay: 400,
        duration: 800,
      },
      idleCalm,
    ])

    dianaCursor.addInstructions([
      idleForAMoment,
      { type: 'moveTo', target: '[data-annotation-id="hero__callout__learn-more1"]', delay: 500, allowHidden: true },
      {
        type: 'action',
        action: 'reveal-annotation',
        target: '[data-annotation-id="hero__callout__learn-more1"]',
        open: true,
        delay: 300,
        allowHidden: true,
      },
      {
        type: 'moveTo',
        relative: { x: 200, y: 150 }, // Move 100px left, 50px down
        delay: 400,
        duration: 800,
      },
      idleStandard,
    ])

    /** Avatars */
    dianaAvatar.addInstructions([idleCalmest])
    janeAvatar.addInstructions([idleCalmest])
    bobAvatar.addInstructions([idleStandard])
    selenaAvatar.addInstructions([idleStandard])
    maggieAvatar.addInstructions([idleStandard])
    gomezAvatar.addInstructions([idleStandard])
  }
}
